import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";
import { CloseOutlined } from "@mui/icons-material";

import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";

import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import {
  Modal,
  Button,
  Typography,
  Box,
  TextField,
  IconButton,
} from "@mui/material";

import { ADD_FUNDS } from "../../../Utils/Mutations";
import { useMutation } from "@apollo/client";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

import CardInput from "./CardInput";
import { useLocation } from "react-router-dom";

const p_Key = process.env.REACT_APP_STRIPE_KEY;
const stripePromise = loadStripe(p_Key);
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { lg: 350, md: 350, sm: 250, xs: 280 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  overflowY: "scroll",
  maxHeight: "80vh",
};


const AddCash = forwardRef(
  ({ text, variant, color, bgcolor, autoAmount, onPaymentSuccess, onPaymentError }, ref) => {
    const [open, setOpen] = React.useState(false);
    const [amount, setAmount] = useState(null);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const location = useLocation();

    const [isInitialized, setIsInitialized] = useState(false);
    const [clientSecret, setClientSecret] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [hasError, setHasError] = useState(false);

    const handleAmountChange = (e) => {
      // Use parseFloat to convert the input value to a float
      const newValue = parseFloat(e.target.value);

      // Check if newValue is a valid number (not NaN)
      if (!isNaN(newValue)) {
        setAmount(newValue);
      } else {
        setAmount(null);
        // Handle invalid input (e.g., display an error message)
      }

      // Clear error when user changes amount
      if (hasError) {
        setHasError(false);
        setErrorMessage("");
      }
    };

    const [initializeWallet, { data, loading }] = useMutation(ADD_FUNDS, {
      onCompleted: (queryData) => {
        try {
          let data = queryData.initializeWalletFunding;
          if (data && data.client_secret) {
            setClientSecret(data.client_secret);
            localStorage.setItem("amount", amount);
            setIsInitialized(true);
            // Clear any previous errors
            setHasError(false);
            setErrorMessage("");
          
          } else {
            throw new Error("Invalid response: missing client_secret");
          }
        } catch (error) {
          console.error(
            "Error processing wallet initialization response:",
            error
          );
          setHasError(true);
          setErrorMessage(
            "Failed to process payment initialization. Please try again."
          );
          setIsInitialized(false);
        }
      },
      onError: (error) => {
        console.error("Wallet initialization error:", error);
        setHasError(true);
        setIsInitialized(false);

        // Handle different types of errors
        if (error.networkError) {
          console.error("Network error:", error.networkError);
          if (error.networkError.statusCode === 401) {
            setErrorMessage("Authentication failed. Please log in again.");
          } else if (error.networkError.statusCode >= 500) {
            setErrorMessage("Server error. Please try again later.");
          } else {
            setErrorMessage(
              "Network error. Please check your connection and try again."
            );
          }
        } else if (error.graphQLErrors?.length > 0) {
          console.error("GraphQL errors:", error.graphQLErrors);
          const firstError = error.graphQLErrors[0];
          // Handle specific GraphQL error messages
          if (firstError.extensions?.code === "INSUFFICIENT_FUNDS") {
            setErrorMessage("Insufficient funds. Please check your balance.");
          } else if (firstError.extensions?.code === "INVALID_AMOUNT") {
            setErrorMessage("Invalid amount. Please enter a valid amount.");
          } else {
            setErrorMessage(
              firstError.message || "Server error occurred. Please try again."
            );
          }
        } else {
          setErrorMessage("An unexpected error occurred. Please try again.");
        }
      },
    });

    const appearance = {
      theme: "stripe",
      variables: {
        colorPrimary: "#2E1834",
      },
    };
    const options = {
      clientSecret,
      appearance,
    };

    useEffect(() => {
      if (autoAmount) {
        setAmount(autoAmount);
        const handleInitializeWallet = async () => {

          if (autoAmount > 1200) return null;
          try {
            setIsInitialized(false);
            setHasError(false);
            setErrorMessage("");

            await initializeWallet({
              variables: {
                amount: autoAmount, // Use autoAmount instead of amount state to avoid timing issues
              },
            });
          } catch (error) {
            console.error("Error in useEffect wallet initialization:", error);
            setHasError(true);
            setIsInitialized(false);
            setErrorMessage(
              "Failed to initialize wallet automatically. Please try manually."
            );
          }
        };

        handleInitializeWallet();
      }
    }, [autoAmount, initializeWallet, open]);

    const handleInitializeWallet = async () => {
      try {
        // Validate amount before proceeding
        if (!amount || amount <= 0) {
          setHasError(true);
          setErrorMessage("Please enter a valid amount.");
          return;
        }

        if (amount > 1200) {
          setHasError(true);
          setErrorMessage("Amount cannot exceed $1200.");
          return;
        }

        // Clear previous errors and initialize
        setIsInitialized(false);
        setHasError(false);
        setErrorMessage("");

       

        await initializeWallet({
          variables: {
            amount: parseFloat(amount), // Ensure amount is a number
          },
        });
      } catch (error) {
        console.error("Error in handleInitializeWallet:", error);
        setHasError(true);
        setIsInitialized(false);

        // Provide user-friendly error message
        if (error.message?.includes("network")) {
          setErrorMessage(
            "Network error. Please check your connection and try again."
          );
        } else {
          setErrorMessage("Failed to initialize payment. Please try again.");
        }
      }
    };

    const formattedAmount = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD", // Replace with your desired currency code
      minimumFractionDigits: 2, // Number of decimal places
    }).format(amount);

    const handleAddReroute = () => {
      localStorage.setItem("mamapurse_reroute", location.pathname);
      handleOpen();
    };
    
    const handlePaymentSuccess = (paymentIntent) => {
      // handleClose();
        onPaymentSuccess(paymentIntent, amount);
    };
    
    const handlePaymentError = (error) => {
      console.error('Payment error:', error);
      // Call the parent's error callback if provided
      if (onPaymentError) {
        onPaymentError(error, amount);
      }
      // Keep the modal open so user can try again
    };

    const buttonRef = useRef(null);

    useImperativeHandle(ref, () => ({
      click: () => {
        buttonRef.current?.click();
      },
    }));

    return (
      <>
        <Button
          ref={buttonRef}
          variant={variant ? variant : "contained"}
          onClick={handleAddReroute}
          sx={{
            background: bgcolor ? bgcolor : "#fff",
            color: color ? color : "#2E1834",
            borderRadius: "40px",
            fontSize: "13px",
            "&:hover": {
              background: "#ffffffb7",
              color: "#2E1834",
            },
          }}
        >
          {text} <AddOutlinedIcon sx={{ fontSize: "13px" }} />
        </Button>
        <Modal
          open={open}
          // onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            {isInitialized ? (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ color: "#333", fontSize: "20px", fontWeight: 500 }}
                  >
                    Add Money
                  </Typography>
                  <IconButton onClick={() => setIsInitialized(false)}>
                    <KeyboardBackspaceIcon sx={{ color: "#333" }} />
                  </IconButton>
                </Box>
                <Box sx={{ mt: 3 }}>
                  {clientSecret && (
                    <Elements options={options} stripe={stripePromise}>
                      <CardInput 
                      onClose={()=>setOpen(false)}
                        amount={amount} 
                        onPaymentSuccess={handlePaymentSuccess}
                        onPaymentError={handlePaymentError}
                      />
                    </Elements>
                  )}
                </Box>
                <Box sx={{ mt: 3 }}>
                  {/* <Button onClick={handleSubmit} variant="contained" fullWidth>{ `Pay ${formattedAmount}`}</Button> */}
                </Box>
              </>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ color: "#333", fontSize: "20px", fontWeight: 500 }}
                  >
                    Add Money
                  </Typography>
                  <IconButton onClick={handleClose}>
                    <CloseOutlined sx={{ color: "#333" }} />
                  </IconButton>
                </Box>
                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    helperText={formattedAmount}
                    placeholder="Amount"
                    value={amount}
                    type="number"
                    onChange={handleAmountChange}
                    InputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      style: {
                        fontSize: "13px",
                        borderRadius: "8px",
                        offset: " 1px solid #f2f2f2",
                        // Replace with your desired font family
                      },
                    }}
                  />
                  {amount > 1200 && (
                    <Typography
                      sx={{
                        color: "#ff0000",
                        fontSize: "14px",
                        mt: 1,
                        textAlign: "center",
                        backgroundColor: "#ffebee",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1px solid #ffcdd2",
                      }}
                    >
                      Your amount cannot exceed $1200
                    </Typography>
                  )}

                  {hasError && errorMessage && (
                    <Typography
                      sx={{
                        color: "#ff0000",
                        fontSize: "12px",
                        mt: 1,
                        textAlign: "center",

                        padding: "10px 12px",
                        borderRadius: "8px",

                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1,
                      }}
                    >
                      {errorMessage}
                    </Typography>
                  )}

                  {loading && (
                    <LoadingButton
                      fullWidth
                      loading
                      loadingPosition="start"
                      startIcon={<SaveIcon />}
                      variant="contained"
                      disabled
                      sx={{ borderRadius: "20px", py: 1, mt: 3 }}
                    >
                      making Payment
                    </LoadingButton>
                  )}
                  {!loading && (
                    <Button
                      fullWidth
                      onClick={handleInitializeWallet}
                      variant="contained"
                      disabled={!amount || amount > 1200 || amount <= 0}
                      sx={{
                        color: "#fff",
                        mt: 3,
                        borderRadius: "100px",
                        backgroundColor: hasError ? "#ff6b6b" : "#2E1834",
                        "&:hover": {
                          backgroundColor: hasError ? "#ff5252" : "#1a0f1f",
                        },
                        "&:disabled": {
                          backgroundColor: "#ccc",
                          color: "#666",
                        },
                        height: "45px",
                        transition: "all 0.3s ease",
                      }}
                    >
                      {hasError ? " Try Again" : "💰 Add Money"}
                    </Button>
                  )}
                </Box>
              </>
            )}{" "}
          </Box>
        </Modal>
      </>
    );
  }
);

export default AddCash;
